import type { BoxShadowData, TextShadowData } from './types';

export type ShadowProperty = 'boxShadow' | 'textShadow';

/** A length as written: Storycards writes vw, people and presets may write px, and a zero may have no unit */
export type ShadowLength = { value: number; unit: 'px' | 'vw' | '' };

/** One shadow of a comma-separated list. Text shadows never have inset or spread. */
export type ShadowLayer = {
	inset: boolean;
	x: ShadowLength;
	y: ShadowLength;
	/** Absent when the source left it out, so an untouched layer is written back as it was */
	blur?: ShadowLength;
	spread?: ShadowLength;
	/** Any CSS color or `var(--sc-token-…)`; empty when the source left it to currentColor */
	color: string;
};

/** A shadow the rows can't show, such as one with a calc() length: kept and edited as written */
export type ShadowCss = { css: string };

export type ShadowEntry = ShadowLayer | ShadowCss;

export const isShadowCss = (entry: ShadowEntry): entry is ShadowCss => 'css' in entry;

export type ShadowLayerKind = 'Drop' | 'Inner' | 'Ring' | 'Glow';

const LENGTH = /^([-+]?(?:\d+(?:\.\d+)?|\.\d+))(px|vw)?$/i;
const COLOR_FUNCTION = /^(rgba?|hsla?|hwb|lab|lch|oklab|oklch|color|color-mix|var)\(/i;

/** Splits on commas outside parentheses: `rgba(0, 0, 0, 0.2)` and `var(--a, #000)` stay whole */
function splitTopLevel(value: string, separator: RegExp): string[] {
	const parts: string[] = [];
	let depth = 0;
	let current = '';
	for (const char of value) {
		if (char === '(') depth += 1;
		if (char === ')') depth -= 1;
		if (depth === 0 && separator.test(char)) {
			parts.push(current);
			current = '';
		} else current += char;
	}
	parts.push(current);
	return parts.map(part => part.trim());
}

function parseLength(entry: string): ShadowLength | undefined {
	const match = entry.match(LENGTH);
	if (!match) return undefined;
	const value = parseFloat(match[1]);
	const unit = (match[2]?.toLowerCase() ?? '') as ShadowLength['unit'];
	// Only zero may drop its unit
	return unit || value === 0 ? { value, unit } : undefined;
}

const isColor = (entry: string) =>
	COLOR_FUNCTION.test(entry) || /^#[0-9a-f]{3,8}$/i.test(entry) || /^[a-z]+$/i.test(entry);

function parseLayer(source: string, property: ShadowProperty): ShadowLayer | undefined {
	const lengths: ShadowLength[] = [];
	let inset = false;
	let color = '';
	// Lengths are one run: `0 2px red 4px` is not a shadow
	let lengthsEnded = false;
	for (const entry of splitTopLevel(source, /\s/).filter(Boolean)) {
		const length = parseLength(entry);
		if (length) {
			if (lengthsEnded) return undefined;
			lengths.push(length);
		} else if (entry.toLowerCase() === 'inset') {
			if (inset || property === 'textShadow') return undefined;
			inset = true;
		} else if (!color && isColor(entry)) {
			color = entry;
			lengthsEnded = lengths.length > 0;
		} else return undefined;
	}
	const maxLengths = property === 'boxShadow' ? 4 : 3;
	if (lengths.length < 2 || lengths.length > maxLengths || (lengths[2]?.value ?? 0) < 0) return undefined;
	const [x, y, blur, spread] = lengths;
	return { inset, x, y, blur, spread, color };
}

/**
 * The shadows of a `box-shadow` or `text-shadow` value, first painted on top; empty for none. A shadow the rows can't
 * show, such as one with a calc() length, stays as its CSS, so the shadows around it are still rows.
 */
export function parseShadowLayers(value: string | undefined, property: ShadowProperty): ShadowEntry[] {
	const trimmed = value?.trim() ?? '';
	if (!trimmed || trimmed === 'none') return [];
	return splitTopLevel(trimmed, /,/).map(part => parseLayer(part, property) ?? { css: part });
}

const formatLength = (length: ShadowLength) => `${length.value}${length.unit}`;

export function stringifyShadowLayers(layers: ShadowEntry[], property: ShadowProperty): string {
	if (!layers.length) return 'none';
	return layers
		.map(layer => {
			if (isShadowCss(layer)) return layer.css;
			const lengths = [layer.x, layer.y];
			if (layer.blur || layer.spread) lengths.push(layer.blur ?? { value: 0, unit: '' });
			if (layer.spread && property === 'boxShadow') lengths.push(layer.spread);
			return [layer.inset && property === 'boxShadow' ? 'inset' : '', ...lengths.map(formatLength), layer.color]
				.filter(Boolean)
				.join(' ');
		})
		.join(', ');
}

/** What a shadow is, read from its values: inset is Inner, a spread with no offset or blur Ring, no offset Glow */
export function getShadowLayerKind(layer: ShadowLayer): ShadowLayerKind {
	if (layer.inset) return 'Inner';
	if (layer.x.value || layer.y.value) return 'Drop';
	return !layer.blur?.value && layer.spread?.value ? 'Ring' : 'Glow';
}

/** How a host shows lengths in the layer controls, which edit plain px numbers */
export type ShadowLengthScale = {
	toPx: (length: ShadowLength | undefined) => number;
	toLength: (px: number) => ShadowLength;
};

/** Lengths kept in px, as Brand Kit presets store them */
export const PX_SCALE: ShadowLengthScale = {
	toPx: length => length?.value ?? 0,
	toLength: px => (px ? { value: px, unit: 'px' } : { value: 0, unit: '' }),
};

export type ShadowEditorData = BoxShadowData | TextShadowData;

/** A layer as the controls edit it: px numbers, and a box shadow's type */
export function toShadowEditorData(
	layer: ShadowLayer,
	property: ShadowProperty,
	scale: ShadowLengthScale
): ShadowEditorData {
	const lengths = { xOffset: scale.toPx(layer.x), yOffset: scale.toPx(layer.y), blur: scale.toPx(layer.blur) };
	if (property === 'textShadow') return { ...lengths, color: layer.color };
	return {
		...lengths,
		shadowType: layer.inset ? 'inset' : 'outset',
		spread: scale.toPx(layer.spread),
		color: layer.color,
	};
}

export function fromShadowEditorData(data: ShadowEditorData, color: string, scale: ShadowLengthScale): ShadowLayer {
	return {
		inset: 'shadowType' in data && data.shadowType === 'inset',
		x: scale.toLength(data.xOffset),
		y: scale.toLength(data.yOffset),
		blur: scale.toLength(data.blur),
		spread: 'spread' in data ? scale.toLength(data.spread) : undefined,
		color,
	};
}
